/**
 * functions common math
 *
 * @category   Javascript
 * @author     Jakob Ohlsen <jakob.ohlsen@gmail.com>
 * @copyright  2025 Jakob Ohlsen
 */
/**
 * **Forward-only** linear interpolation with an amortised cursor.
 *
 * Like `linearInterpolationOfArrays`, but instead of starting the segment
 * scan at `i = 0` every call, it starts at the cursor `inter.index` and
 * walks forward, then updates `inter.index` to the matched segment so the
 * next call can pick up where this one left off. This makes a sequence of
 * monotonically-non-decreasing target queries amortised O(N) instead of
 * O(N²) across all calls — that's what the pipeline relies on.
 *
 * ⚠️ **CONTRACT — read before using.**
 *   - The cursor advances forward only. It does NOT rewind.
 *   - Callers MUST feed monotonically non-decreasing `inter.target` values
 *     within a single pass.
 *   - If `inter.target < x[inter.index]`, the function returns `null` —
 *     it cannot find the matching segment because the cursor has already
 *     moved past it. This is BY DESIGN, not a bug.
 *   - Between independent passes (e.g. starting a second forward scan
 *     over the same data), reset `inter.index = 0` manually.
 *
 * If you want a stateless lookup that handles arbitrary targets, use
 * {@link linearInterpolationOfArrays} instead.
 *
 * Previously named `optimizedLinearInterpolationOfArrays`; renamed so the
 * "forward-only" property is visible at every call site.
 */
export function forwardLinearInterpolationOfArrays(x, y, inter) {
    for (let i = inter.index; i < x.length - 1; i++) {
        const p1x = x[i];
        const p1y = y[i];
        const p2x = x[i + 1];
        const p2y = y[i + 1];
        if (inter.target >= p1x && inter.target <= p2x) {
            const t = (inter.target - p1x) / (p2x - p1x);
            const ret = p1y + t * (p2y - p1y);
            inter.index = i;
            return ret;
        }
    }
    return null;
}
/**
 * Linearly interpolates `y` at `targetX` over the piecewise-linear graph
 * defined by parallel arrays `x` and `y`.
 *
 * Range is inclusive on both ends: `[x[0], x[x.length - 1]]`.
 *   - `targetX === x[0]`         → returns `y[0]`.
 *   - `targetX === x[x.length-1]`→ returns `y[y.length-1]` (upper-bound fast path).
 *   - `targetX <  x[0]`          → returns `null`.
 *   - `targetX >  x[x.length-1]` → returns `null`.
 *   - `x.length === 1`           → returns `y[0]` iff `targetX === x[0]`, else `null` (single-knot fast path).
 *   - `x.length === 0`           → returns `null`.
 *
 * Use {@link linearInterpolationOfArraysWithExtrapolationOutOfRange} when you
 * want extrapolation past the endpoints instead of `null`.
 *
 * Pre-conditions (not validated for hot-loop performance):
 *   - `x` is sorted ascending.
 *   - `x.length === y.length`.
 *
 * Loop uses strict `targetX < p2x` so each knot belongs to exactly one
 * segment — the one starting at it — disambiguating segment ownership. The
 * upper-bound `targetX === x[n-1]` fast path covers the closed-right end.
 */
export function linearInterpolationOfArrays(x, y, targetX) {
    const n = x.length;
    if (n === 0)
        return null;
    // Single-knot fast path. Exact match returns y[0]; anything else is out
    // of range (no segment to interpolate over).
    if (n === 1)
        return targetX === x[0] ? y[0] : null;
    // Upper-bound fast path — exact match at the last knot.
    if (targetX === x[n - 1])
        return y[n - 1];
    for (let i = 0; i < n - 1; i++) {
        const p1x = x[i];
        const p2x = x[i + 1];
        if (targetX >= p1x && targetX < p2x) {
            const p1y = y[i];
            const p2y = y[i + 1];
            const t = (targetX - p1x) / (p2x - p1x);
            return p1y + t * (p2y - p1y);
        }
    }
    return null;
}
/**
 * Like {@link linearInterpolationOfArrays} but extrapolates linearly from the
 * first / last segment when `targetX` falls outside `[x[0], x[x.length-1]]`.
 *
 * Edge cases:
 *   - `x.length === 1` with `targetX === x[0]` → returns `y[0]` (single-knot
 *     fast path). Extrapolation away from a single knot is undefined (no
 *     slope) so any other `targetX` throws.
 *   - `x.length === 0` → throws.
 *   - `x.length !== y.length` → throws.
 */
export function linearInterpolationOfArraysWithExtrapolationOutOfRange(x, y, targetX) {
    if (x.length !== y.length) {
        throw new Error('x and y arrays must have the same length');
    }
    const n = x.length;
    if (n === 0) {
        throw new Error('At least one point is required');
    }
    // Single-knot fast path. Exact match returns y[0]; any off-knot target
    // is undefined (extrapolation needs a slope, which requires ≥2 points).
    if (n === 1) {
        if (targetX === x[0])
            return y[0];
        throw new Error('At least two points are required for extrapolation');
    }
    // Upper-bound fast path — exact match at the last knot.
    if (targetX === x[n - 1])
        return y[n - 1];
    for (let i = 0; i < n - 1; i++) {
        const p1x = x[i];
        const p2x = x[i + 1];
        if (targetX >= p1x && targetX < p2x) {
            const p1y = y[i];
            const p2y = y[i + 1];
            const t = (targetX - p1x) / (p2x - p1x);
            return p1y + t * (p2y - p1y);
        }
    }
    // Below first knot — extrapolate from the first segment.
    if (targetX < x[0]) {
        const t = (targetX - x[0]) / (x[1] - x[0]);
        return y[0] + t * (y[1] - y[0]);
    }
    // Above last knot — extrapolate from the last segment.
    const t = (targetX - x[n - 2]) / (x[n - 1] - x[n - 2]);
    return y[n - 2] + t * (y[n - 1] - y[n - 2]);
}
/**
 * Evaluate a piecewise series defined by parallel ascending-`x` arrays at an
 * arbitrary `targetX`, using the rule that matches how the series is drawn —
 * so a hovered crosshair reads the same value the chart shows:
 *
 *   - `'linear'`  : linear interpolation between bracketing knots (weather,
 *                   elevation, dense computed metrics). Out of range → `null`.
 *   - `'stepped'` : forward-fill / hold-last — the value of the largest knot
 *                   `x[i] ≤ targetX` (power, road crr, cda; matches Chart.js
 *                   `stepped: true` and the simulation's `prescribedPowerAt`).
 *                   Below `x[0]` → `null`; at/after the last knot → `y[n-1]`.
 *   - `'discrete'`: the nearest knot within `opts.discreteToleranceX`
 *                   (nutrition product events — not a continuous series). No
 *                   event within tolerance → `null`.
 *
 * Pre-conditions (not validated for hot-loop performance):
 *   - `x` is sorted ascending.
 *   - `x.length === y.length`.
 */
export function evaluateSeriesAtX(x, y, targetX, mode, opts) {
    const n = x.length;
    if (n === 0)
        return null;
    if (mode === 'discrete') {
        const tol = opts?.discreteToleranceX ?? 0;
        // Lower bound: first index with x[lo] >= targetX, then pick the closer neighbour.
        let lo = 0;
        let hi = n - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (x[mid] < targetX)
                lo = mid + 1;
            else
                hi = mid;
        }
        let best = lo;
        if (lo > 0 && Math.abs(x[lo - 1] - targetX) <= Math.abs(x[lo] - targetX))
            best = lo - 1;
        return Math.abs(x[best] - targetX) <= tol ? y[best] : null;
    }
    // Single-knot fast path.
    if (n === 1) {
        if (mode === 'stepped')
            return targetX >= x[0] ? y[0] : null;
        return targetX === x[0] ? y[0] : null; // linear
    }
    if (targetX < x[0])
        return null; // below the first knot (both modes)
    if (targetX === x[n - 1])
        return y[n - 1]; // exact last knot (both modes)
    // Beyond the last knot: stepped holds the last value, linear is out of range.
    if (targetX > x[n - 1])
        return mode === 'stepped' ? y[n - 1] : null;
    // Largest index i with x[i] <= targetX (so x[i] <= targetX < x[i+1]).
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (x[mid] <= targetX)
            lo = mid + 1;
        else
            hi = mid;
    }
    const i = lo - 1;
    if (mode === 'stepped')
        return y[i];
    const p1x = x[i];
    const p2x = x[i + 1];
    const t = (targetX - p1x) / (p2x - p1x);
    return y[i] + t * (y[i + 1] - y[i]);
}
export function isNumeric(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}
export function closest(num, arr) {
    if (arr.length === 0)
        return -1;
    let left = 0;
    let right = arr.length - 1;
    // Binary search to find the closest index
    while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (arr[mid] === num) {
            return mid;
        }
        if (arr[mid] < num) {
            left = mid + 1;
        }
        else {
            right = mid;
        }
    }
    // Now left is the first index where arr[left] >= num
    if (left === 0)
        return 0;
    if (left === arr.length)
        return arr.length - 1;
    // Compare arr[left] and arr[left - 1] to see which is closer
    const prev = arr[left - 1];
    const next = arr[left];
    return Math.abs(prev - num) <= Math.abs(next - num) ? left - 1 : left;
}
export function inLimits(minValue, maxValue, value) {
    return Math.max(Math.min(value, maxValue), minValue);
}
export function arraysEqual(a, b) {
    return (Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => val === b[index]));
}
